import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetPropertiesListQuery } from '@/redux/internal-services/workspaceApiExtensions';
import { Spinner } from '@/components/UI/Spinner';
import { Pagination } from '@/components/UI/Pagination';
import WorkspacePropertyCard from './WorkspacePropertyCard';
import { WORKSPACE_QUERY_OPTIONS } from '@/components/chat/workspaceQueryOptions';
import { useWorkspaceFilterReport, type OnWorkspaceFilterChange } from '@/hooks/useWorkspaceFilterReport';
import { setVisibleRows } from '@/components/chat/visibleListRows';

interface WorkspacePropertyListProps {
  initialSearch?: string;
  /** 'active' pre-selects the Active filter chip (set by search_engagement/SAM's "show me the
   *  engagements" flow) — omitted/'all' starts on the All chip, same as a plain properties ask. */
  initialEngagementStatus?: 'active' | 'all';
  /** Free-text engagement status/stage filter set by search_engagement (e.g. 'Prospects', 'Job
   *  Scheduled') — agent-driven, but shown/clearable as its own chip alongside All/Active below. */
  initialStatus?: string;
  /** Structured, exact-field address filters set by search_engagement's partial-address browse
   *  (e.g. address_state="NM" alone → "properties in NM") — distinct from `initialSearch`'s fuzzy
   *  free-text match. Agent-driven; shown/clearable as their own chips. */
  initialCity?: string;
  initialState?: string;
  initialPostalCode?: string;
  /** Structured property-level filters set by search_engagement's tenant-wide browse — a
   *  DIFFERENT axis from the engagement filters above (property_type/property_status describe
   *  the PROPERTY itself, not any engagement on it). Agent-driven; shown/clearable as chips. */
  initialPropertyType?: 'residential' | 'commercial' | 'industrial';
  initialPropertyStatus?: string;
  /** Free-text engagement filters set by search_engagement — engagement_type/source are exact,
   *  case-insensitive matches; the date pairs are inclusive ranges on created_on/updated_on.
   *  Agent-driven; shown/clearable as chips. */
  initialEngagementType?: string;
  initialSource?: string;
  initialCreatedAfter?: string;
  initialCreatedBefore?: string;
  initialUpdatedAfter?: string;
  initialUpdatedBefore?: string;
  /** Assignee/contact filters set by search_engagement — `initialAssignee`/`initialContactName`
   *  are names (or an already-resolved id), resolved server-side; the REST route re-resolves them
   *  the same way search_engagement's own tool does. Agent-driven; shown/clearable as chips. */
  initialAssignee?: string;
  /** Display name for `initialAssignee` when it is a raw user id — chip text only, never sent. */
  initialAssigneeLabel?: string;
  initialAssigneeRole?: 'sales_agent' | 'project_manager' | 'assigned_user';
  initialContactName?: string;
  initialContactRole?: string;
  onSelectProperty?: (propertyId: string) => void;
  /** Set when the user clicks a specific engagement sub-card rather than the property card
   *  itself — lets the caller open the property detail landing directly on that engagement
   *  instead of its Engagement tab's multi-engagement picker. */
  onSelectEngagement?: (propertyId: string, engagementId: string) => void;
  /** Workspace mode: report a MANUALLY changed filter so it joins the "⋯" recent-views menu
   *  alongside the agent-driven views. See `useWorkspaceFilterReport`. */
  onFiltersChange?: OnWorkspaceFilterChange;
}

const LIMIT = 20;

/** Agent-driven filters search_engagement can set — mirrored into local state (seeded once from
 * props at mount) so each one can carry its own "×" to clear, same as a user-set filter would.
 * The component remounts on every new SAM filter and every "⋯" replay (see UXView's `listKey` —
 * card id + filter — on this component), so a plain lazy useState initializer is enough — no
 * prop-sync effect needed. */
interface ExtraFilters {
  status?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  propertyType?: 'residential' | 'commercial' | 'industrial';
  propertyStatus?: string;
  engagementType?: string;
  source?: string;
  createdAfter?: string;
  createdBefore?: string;
  updatedAfter?: string;
  updatedBefore?: string;
  assignee?: string;
  assigneeLabel?: string;
  assigneeRole?: 'sales_agent' | 'project_manager' | 'assigned_user';
  contactName?: string;
  contactRole?: string;
}

const WorkspacePropertyList: React.FC<WorkspacePropertyListProps> = ({
  initialSearch, initialEngagementStatus, initialStatus, initialCity, initialState, initialPostalCode,
  initialPropertyType, initialPropertyStatus, initialEngagementType, initialSource,
  initialCreatedAfter, initialCreatedBefore, initialUpdatedAfter, initialUpdatedBefore,
  initialAssignee, initialAssigneeLabel, initialAssigneeRole, initialContactName, initialContactRole,
  onSelectProperty, onSelectEngagement, onFiltersChange,
}) => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState(initialSearch ?? '');
  const [search, setSearch] = useState(initialSearch ?? '');
  const [engagementStatus, setEngagementStatus] = useState<'active' | 'all'>(initialEngagementStatus ?? 'all');
  const [page, setPage] = useState(0);
  const [extra, setExtra] = useState<ExtraFilters>(() => ({
    status: initialStatus || undefined,
    city: initialCity || undefined,
    state: initialState || undefined,
    postalCode: initialPostalCode || undefined,
    propertyType: initialPropertyType || undefined,
    propertyStatus: initialPropertyStatus || undefined,
    engagementType: initialEngagementType || undefined,
    source: initialSource || undefined,
    createdAfter: initialCreatedAfter || undefined,
    createdBefore: initialCreatedBefore || undefined,
    updatedAfter: initialUpdatedAfter || undefined,
    updatedBefore: initialUpdatedBefore || undefined,
    assignee: initialAssignee || undefined,
    assigneeLabel: initialAssigneeLabel || undefined,
    assigneeRole: initialAssigneeRole || undefined,
    contactName: initialContactName || undefined,
    contactRole: initialContactRole || undefined,
  }));

  const clearExtra = (...keys: (keyof ExtraFilters)[]) => {
    setExtra((prev) => {
      const next = { ...prev };
      for (const k of keys) delete next[k];
      return next;
    });
    setPage(0);
  };

  // Debounce the search box so every keystroke doesn't refetch — reset to page 1 on a new search.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Shared by the query and the recent-views report, so a "⋯" entry replays exactly what's shown.
  const queryArgs = {
    search: search || undefined,
    engagement_status: engagementStatus === 'active' ? ('active' as const) : undefined,
    status: extra.status,
    city: extra.city,
    state: extra.state,
    postal_code: extra.postalCode,
    property_type: extra.propertyType,
    property_status: extra.propertyStatus,
    engagement_type: extra.engagementType,
    source: extra.source,
    created_after: extra.createdAfter,
    created_before: extra.createdBefore,
    updated_after: extra.updatedAfter,
    updated_before: extra.updatedBefore,
    assignee: extra.assignee,
    assignee_role: extra.assigneeRole,
    contact_name: extra.contactName,
    contact_role: extra.contactRole,
    skip: page * LIMIT,
    limit: LIMIT,
  };

  // Every filter here is agent-driven — without a visible, clearable chip the panel silently
  // narrows to whatever SAM asked for and the user has no way to tell (beyond re-reading the
  // chat message) or undo it. Built straight from `extra` state (which mirrors what's actually
  // being sent), not from SAM's own title/caption text, so it can never drift from what's really
  // filtering the list. Each chip clears only itself (and its paired role, where one exists —
  // a role with no name left to qualify is meaningless).
  const activeFilterChips: { key: string; label: string; value: string; onClear: () => void }[] = [
    extra.status && {
      key: 'status', label: t('properties.activeFilters.status'), value: extra.status,
      onClear: () => clearExtra('status'),
    },
    extra.engagementType && {
      key: 'engagementType', label: t('properties.activeFilters.engagementType'), value: extra.engagementType,
      onClear: () => clearExtra('engagementType'),
    },
    extra.propertyType && {
      key: 'propertyType', label: t('properties.activeFilters.propertyType'), value: extra.propertyType,
      onClear: () => clearExtra('propertyType'),
    },
    extra.propertyStatus && {
      key: 'propertyStatus', label: t('properties.activeFilters.propertyStatus'), value: extra.propertyStatus,
      onClear: () => clearExtra('propertyStatus'),
    },
    extra.source && {
      key: 'source', label: t('properties.activeFilters.source'), value: extra.source,
      onClear: () => clearExtra('source'),
    },
    extra.createdAfter && {
      key: 'createdAfter', label: t('properties.activeFilters.createdAfter'), value: extra.createdAfter,
      onClear: () => clearExtra('createdAfter'),
    },
    extra.createdBefore && {
      key: 'createdBefore', label: t('properties.activeFilters.createdBefore'), value: extra.createdBefore,
      onClear: () => clearExtra('createdBefore'),
    },
    extra.updatedAfter && {
      key: 'updatedAfter', label: t('properties.activeFilters.updatedAfter'), value: extra.updatedAfter,
      onClear: () => clearExtra('updatedAfter'),
    },
    extra.updatedBefore && {
      key: 'updatedBefore', label: t('properties.activeFilters.updatedBefore'), value: extra.updatedBefore,
      onClear: () => clearExtra('updatedBefore'),
    },
    extra.assignee && {
      key: 'assignee',
      label: extra.assigneeRole
        ? t(`properties.activeFilters.assigneeRole${extra.assigneeRole === 'sales_agent' ? 'SalesAgent' : extra.assigneeRole === 'project_manager' ? 'ProjectManager' : 'AssignedUser'}`)
        : t('properties.activeFilters.assignee'),
      value: extra.assigneeLabel || extra.assignee,
      onClear: () => clearExtra('assignee', 'assigneeLabel', 'assigneeRole'),
    },
    extra.contactName && {
      key: 'contactName', label: t('properties.activeFilters.contactName'), value: extra.contactName,
      onClear: () => clearExtra('contactName', 'contactRole'),
    },
    extra.contactRole && {
      key: 'contactRole', label: t('properties.activeFilters.contactRole'), value: extra.contactRole,
      onClear: () => clearExtra('contactRole'),
    },
    extra.city && {
      key: 'city', label: t('properties.activeFilters.city'), value: extra.city,
      onClear: () => clearExtra('city'),
    },
    extra.state && {
      key: 'state', label: t('properties.activeFilters.state'), value: extra.state,
      onClear: () => clearExtra('state'),
    },
    extra.postalCode && {
      key: 'postalCode', label: t('properties.activeFilters.postalCode'), value: extra.postalCode,
      onClear: () => clearExtra('postalCode'),
    },
  ].filter((c): c is { key: string; label: string; value: string; onClear: () => void } => Boolean(c));

  const { data, isLoading, isFetching, isError } = useGetPropertiesListQuery(queryArgs, WORKSPACE_QUERY_OPTIONS);

  // Report user-driven filter changes (search box, Active/All chip, clearing an agent-set chip)
  // to the "⋯" recent-views menu.
  useWorkspaceFilterReport(queryArgs, onFiltersChange);

  const properties = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  // Publish the rows the user sees (one per engagement, an engagement-less property counts once) so
  // SAM's navigate_cards "open the 3rd one" / "next" counts the same rows as the user.
  useEffect(() => {
    if (isLoading || isFetching) return;
    setVisibleRows('properties', (data?.data ?? []).flatMap((p) => (p.engagements?.length
      ? p.engagements.map((e) => ({ id: p.property_id, engagementId: e.engagement_id }))
      : [{ id: p.property_id }])));
  }, [data, isLoading, isFetching]);

  return (
    <div className="flex flex-col min-h-full space-y-2">
      {/* Search box */}
      <div className="relative">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ui-text3">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('properties.searchPlaceholder')}
          className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-ui-border bg-ui-subtle text-ui-text placeholder:text-ui-text3 focus:outline-none focus:border-ui-ring/40"
        />
      </div>

      {/* One combined row: the All/Active toggle (user-driven) alongside every agent-driven
          filter currently narrowing the panel — each with its own "×" to clear just that one. */}
      <div className="flex flex-wrap gap-1.5">
        {(['all', 'active'] as const).map((value) => {
          const active = engagementStatus === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => { setEngagementStatus(value); setPage(0); }}
              className={`flex-shrink-0 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors whitespace-nowrap ${
                active
                  ? 'bg-accent/10 border-accent/30 text-accent-text'
                  : 'border-ui-border bg-ui-subtle text-ui-text2 hover:border-accent/25 hover:text-ui-text'
              }`}
            >
              {value === 'all' ? 'All' : 'Active engagements'}
            </button>
          );
        })}
        {activeFilterChips.map((chip) => (
          <span
            key={chip.key}
            className="flex-shrink-0 flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full border border-accent/20 bg-accent-soft/40 text-xs font-medium text-accent-text whitespace-nowrap"
          >
            {chip.label}: {chip.value}
            <button
              type="button"
              onClick={chip.onClear}
              aria-label={t('properties.activeFilters.clear', { label: chip.label })}
              className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-accent/15 text-accent-text"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </span>
        ))}
      </div>

      {total > 0 && (
        <p className="text-xs text-ui-text3 px-0.5">
          Showing {page * LIMIT + 1}-{Math.min(page * LIMIT + properties.length, total)} of {total}
        </p>
      )}

      {(isLoading || isFetching) && <div className="flex justify-center py-12"><Spinner /></div>}
      {isError && (
        <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-4">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-ui-text3"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <p className="text-sm font-medium text-ui-text2">Nothing to show</p>
        </div>
      )}
      {!isLoading && !isFetching && !isError && properties.length === 0 && (
        <p className="text-center text-ui-text3 py-8">
          {engagementStatus === 'active' ? t('properties.noActiveEngagementsFound') : t('properties.noEngagementsFound')}
        </p>
      )}

      {!(isLoading || isFetching) && (
        <div className="grid grid-cols-1 gap-3 pb-2">
          {properties.map((p) => (
            <WorkspacePropertyCard key={p.property_id} property={p} onSelect={onSelectProperty} onSelectEngagement={onSelectEngagement} />
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
};

export default WorkspacePropertyList;
